import React, { ReactElement } from 'react';
import {
  IProductDelivery,
  IOrderFinances,
  TShipmentDestinationPlace,
  IShipmentByStatusItem,
  IWarehouseTemporaryStorage
} from 'types/orders';
import { groupByKey } from 'utils/groupByKey';
import { copyClipboard } from 'utils/copyClipboard';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { IrawSelectOption } from 'components/ModuleFilters/ModuleFiltersExpandableSelect';
import {
  SLA_ETA_OPTION_COLOR_MAP,
  TRawFilterValue
} from './components/OrdersTrackingOperationsFilters/filter.constants';
import { CONFIG_STATUS_NAME } from 'types/product.configuration.types';

export const ORDER_PROPERTY_TO_FILTER_KEYS = {
  ORDER_NUMBER: 'ORDER_NUMBER',
  ORGANIZATION_NAME: 'ORGANIZATION_NAME',
  EMPLOYEE_NAME: 'EMPLOYEE_NAME',
  SERIAL_NUMBER: 'SERIAL_NUMBER',
  PURCHASE_ORDER: 'PURCHASE_ORDER',
  SKU_NUMBER: 'SKU_NUMBER'
} as const;

export const SEARCH_TYPE_TO_FILTER_CONFIG: Record<string, { name: string; includes: string }> = {
  [ORDER_PROPERTY_TO_FILTER_KEYS.ORDER_NUMBER]: {
    name: 'order.id',
    includes: '$eq'
  },
  [ORDER_PROPERTY_TO_FILTER_KEYS.ORGANIZATION_NAME]: {
    name: 'order.organizationName',
    includes: '$like'
  },
  [ORDER_PROPERTY_TO_FILTER_KEYS.EMPLOYEE_NAME]: {
    name: 'shipment.receiverName',
    includes: '$like'
  },
  [ORDER_PROPERTY_TO_FILTER_KEYS.SERIAL_NUMBER]: {
    name: 'sale.serial',
    includes: '$like'
  },
  [ORDER_PROPERTY_TO_FILTER_KEYS.PURCHASE_ORDER]: {
    name: 'order.purchaseOrder',
    includes: '$like'
  },
  [ORDER_PROPERTY_TO_FILTER_KEYS.SKU_NUMBER]: {
    name: 'product.sku',
    includes: '$like'
  }
};

/**
 * Acorta nombres de países largos para mejorar la visualización en la UI.
 * @param countryName - Nombre completo del país
 * @returns Nombre del país acortado si aplica, o el original
 */
export const getShortCountryName = (countryName?: string): string => {
  if (!countryName) return '';
  return countryName.replace('Estados Unidos de América', 'Estados Unidos');
};

/**
 * Genera el nombre a mostrar según el tipo de destino del envío.
 * @param destinationPlace - Tipo de destino ('user', 'nudos', 'office')
 * @param receiverName - Nombre del receptor (usado cuando es 'user')
 * @param countryName - Nombre del país de destino (usado para 'nudos' y 'office')
 * @returns Nombre formateado según el tipo de destino
 */
export const getDestinationDisplayName = (
  destinationPlace?: TShipmentDestinationPlace | string,
  receiverName?: string,
  countryName?: string
): string => {
  if (!destinationPlace) return 'Sin destino';

  const countryShortName = getShortCountryName(countryName);

  switch (destinationPlace as TShipmentDestinationPlace) {
    case 'user':
      return receiverName || 'Sin nombre';
    case 'nudos':
      return `Bodega ${countryShortName}`.trim();
    case 'office':
      return `Oficina ${countryShortName}`.trim();
    default:
      return 'Sin destino';
  }
};

export const getWarehouseTemporaryStorageReasonNames = (
  entries: IWarehouseTemporaryStorage[] | undefined
): string[] => {
  if (!entries?.length) return [];

  const names: string[] = [];

  for (const entry of entries) {
    for (const item of entry.warehouseTemporaryStorageReasons ?? []) {
      const name = item.temporaryStorageReason?.name?.trim();
      if (name) names.push(name);
    }
  }

  return names;
};

/**
 * Agrupa los productos del envío por SKU.
 * @param products - Lista de productos del envío
 * @returns Objeto con SKU como clave y array de productos como valor
 */
export const groupProductsBySku = (products: IProductDelivery[]): Record<string, IProductDelivery[]> => {
  return products.reduce((acc, product) => {
    const sku = product.sku || 'sin-sku';

    if (!acc[sku]) {
      acc[sku] = [];
    }

    acc[sku].push(product);

    return acc;
  }, {} as Record<string, IProductDelivery[]>);
};

export const copyTextToClipboard = (text: string) => {
  copyClipboard({ text, notificationMessage: 'Copiado al portapapeles' });
};

/**
 * Devuelve una lista con una sola entrada por orderId (p. ej. cuando hay varios envíos por orden).
 * Se conserva la primera ocurrencia de cada orderId.
 * @param ordersList - Lista de order finances que puede contener varios ítems por orderId
 * @returns Lista con una sola entrada por orderId
 */
export const getUniqueOrderFinancesByOrderId = (ordersList: IOrderFinances[]): IOrderFinances[] => {
  const seen = new Set<string | number>();
  return ordersList.filter(item => {
    if (seen.has(item.orderId)) return false;
    seen.add(item.orderId);
    return true;
  });
};

export const formatOrderFinances = (ordersList: IOrderFinances[]): IOrderFinances[] =>
  ordersList.map(order => ({ ...order, id: JSON.stringify(order.orderId) }));
export const extractValueFromOption = (option: IrawSelectOption): string => {
  return option.value || '';
};

export const extractNameFromOption = (option: IrawSelectOption): string => {
  return option.name || '';
};

export const stringToBoolean = (value: string): boolean | null => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

/**
 * Agrega iconos con colores a las opciones del filtro de SLA y ETA.
 * @param options - Opciones del filtro sin iconos
 * @returns Opciones con iconos agregados
 */
export const addIconsToSlaEtaOptions = (options: IrawSelectOption[]): IrawSelectOption[] => {
  return options.map(option => {
    const color = SLA_ETA_OPTION_COLOR_MAP.get(option.name);
    if (!color) return option;

    return {
      ...option,
      icon: React.createElement(
        'span',
        { className: 'slaEtaOptionIcon' },
        React.createElement(BordOneToneIcon, {
          variant: 'clockCircle',
          standardSize: 16,
          stroke: color,
          fill: color
        })
      ) as ReactElement
    };
  });
};

export const isStringArray = (v: TRawFilterValue): v is string[] => {
  return Array.isArray(v) && (v.length === 0 || typeof v[0] === 'string');
};

export const isNumberArray = (v: TRawFilterValue): v is number[] => {
  return Array.isArray(v) && (v.length === 0 || typeof v[0] === 'number');
};

export const isCountryNameMatching = (cardCountryName: string, selectedName: string): boolean => {
  const a = (cardCountryName || '').trim();
  const b = (selectedName || '').trim();

  if (!a || !b) return false;
  if (a === b) return true;

  const isWordBoundary = (str: string, offset: number): boolean => {
    const c = str.slice(offset, offset + 1);
    return !c || !/^[a-zA-Z\u00C0-\u024F]$/.test(c);
  };

  if (a.startsWith(b) && isWordBoundary(a, b.length)) return true;
  if (b.startsWith(a) && isWordBoundary(b, a.length)) return true;

  return false;
};

const getDestinationAndStatusKeyForOperations = (shipment: IShipmentByStatusItem): string => {
  const destinationKey = [
    shipment.destinationPlace ?? '',
    shipment.destinationAddress ?? '',
    shipment.city ?? '',
    shipment.zipCode ?? '',
    shipment.receiverName ?? '',
    shipment.destinationCountryId ?? ''
  ].join('|');

  const statusId = shipment.shipmentStatusLogs?.[0]?.shipmentStatusId ?? '';

  return `${destinationKey}|${statusId}`;
};

export const groupShipmentsByDestinationAndStatus = (shipments: IShipmentByStatusItem[]): IShipmentByStatusItem[][] => {
  return groupByKey(shipments, getDestinationAndStatusKeyForOperations);
};

export const CONFIG_STATUS_CLASS_MAP: Record<CONFIG_STATUS_NAME, string> = {
  [CONFIG_STATUS_NAME.TO_COMPLETE]: 'shipmentItemConfigurationTooltip__line--toComplete',
  [CONFIG_STATUS_NAME.NOT_COMPLETED]: 'shipmentItemConfigurationTooltip__line--notCompleted',
  [CONFIG_STATUS_NAME.COMPLETED]: 'shipmentItemConfigurationTooltip__line--completed'
};

export const CONFIG_STATUS_ORDER = Object.values(CONFIG_STATUS_NAME);
